import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';

import Modal from 'react-modal';
import NumberFormat from 'react-number-format';
import dayjs from 'dayjs';
import { bool, func, string } from 'prop-types';

import EditMarketplaceAllocation from './EditMarketplaceAllocation';
import {
  getDspPacingData,
  getEscrowBalanceMarketplaceData,
  storeAllocatedBudget,
} from '../../../../../../api';
import {
  CloseIcon,
  AddIcons,
  InfoRedIcon,
  LeftArrowIcon,
} from '../../../../../../theme/images';
import {
  ModalBox,
  Button,
  InputFormField,
  PageLoader,
  AllocateBar,
  ErrorMsgBox,
  Tabs,
} from '../../../../../../common';

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    maxWidth: '600px ',
    minHeight: '220px',
    width: '100% ',
    overlay: ' {zIndex: 1000}',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
  },
};
export default function EscrowBudgetAllocationModal({
  id,
  isOpen,
  onClick,
  customerId,
  marketplace,
}) {
  const mounted = useRef(false);
  const currencySymbol = '$';
  const currentDate = useMemo(() => {
    return new Date();
  }, []);
  currentDate.setDate(1);
  currentDate.setHours(currentDate.getHours() + 12);
  const newCurrentDate = useMemo(() => {
    return new Date();
  }, []);
  newCurrentDate.setDate(1);
  newCurrentDate.setHours(currentDate.getHours());
  const currentMonthYear = dayjs(newCurrentDate).format('YYYY-MM-DD');

  const [escrowBalanceMarketplace, setEscrowBalanceMarketplace] = useState([]);
  const [oldEscrowAllocation, setOldEscrowAllocation] = useState([]);
  const [selectedMarketplace, setSelectedMarketplace] = useState(marketplace);
  const [totalEscrowBalance, setTotalEscrowBalance] = useState();
  const [selectedMarketplaceBalance, setSelectedMarketplaceBalance] =
    useState();
  const [showEscrowMonthlyAllocation, setShowEscrowMonthlyAllocation] =
    useState(true);
  const [showMarketPlaceAllocation, setShowMarketPlaceAllocation] =
    useState(false);
  const [allocatedMonths, setAllocatedMonths] = useState([]);
  const [isAllowToEdit, setIsAllowToEdit] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [isMonthlyDataLoading, setIsMonthlyDataLoading] = useState(true);
  const [isApiCall, setIsApiCall] = useState(false);
  const [isEscrowBalanceExceed, setIsEscrowBalanceExceed] = useState(
    totalEscrowBalance < 0,
  );
  const [escrowAllocatedMonthIndex, setEscrowAllocatedMonthIndex] =
    useState(null);

  // thousand separator for numbers with currency symbol
  const addThousandSeperator = (value, type = '') => {
    if (value && value !== null && value !== 0) {
      value = Number(value.toFixed(2));
      return `${value < 0 ? '-' : ''}${
        type === 'currency' ? currencySymbol : ''
      }${value
        .toString()
        .replace('-', '')
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
    }
    return 0;
  };

  // bind escrow balance data of marketplaces
  const bindEscrowMarketplaceData = useCallback((response) => {
    const tempData = [];
    if (response) {
      setTotalEscrowBalance(
        response?.total_escrow ? response?.total_escrow : 0,
      );
      if (response?.marketplace?.length) {
        response.marketplace.forEach((item, index) => {
          tempData.push({
            label: item?.label ? item?.label : null,
            value: item?.value ? item?.value : null,
            key: item?.id ? item.id : `${item.label}${index}`,
            escrowBalance: item?.total ? item?.total : 0,
            escrowReallocatedBalance: item?.total ? item?.total : 0,
          });
          setOldEscrowAllocation(tempData);
        });
      }
    }
    return tempData;
  }, []);

  // get escrow balance of various marketplaces
  const getEscrowBalanceMarketplace = useCallback(() => {
    setIsDataLoading(true);
    getEscrowBalanceMarketplaceData(customerId).then((response) => {
      if (mounted.current) {
        if (response?.status === 200) {
          const getData = bindEscrowMarketplaceData(response?.data);
          setEscrowBalanceMarketplace(getData);
          const data = getData.filter(
            (item) => item.value === selectedMarketplace,
          );
          setIsAllowToEdit(data[0]?.escrowBalance >= 0);
        }
        setIsDataLoading(false);
      }
    });
  }, [bindEscrowMarketplaceData, customerId, selectedMarketplace]);

  // bind escrow allocated balance data
  const bindEscrowAllocatedBalanceData = useCallback((response) => {
    const tempEscrowData = [];
    for (const item of response) {
      tempEscrowData.push({
        escrow_allocated: item?.escrow_allocated,
        escrow_allocated_converted_usd: item?.escrow_allocated_converted_usd,
        escrow_allocated_converted_usd_old:
          item?.escrow_allocated_converted_usd,
        month_year: item?.month_year,
      });
    }
    return tempEscrowData;
  }, []);

  // get monthly allocated data from dsp pacing api
  const getDSPPacing = useCallback(
    (currentMarketplace) => {
      setIsMonthlyDataLoading(true);
      setAllocatedMonths([]);
      getDspPacingData(customerId, currentMarketplace).then((res) => {
        if (mounted.current) {
          if (res && res.status === 200) {
            const dspPacing = res?.data?.dsp_pacing;
            const currentEscrowBalance = dspPacing?.escrow_converted_usd
              ? dspPacing?.escrow_converted_usd
              : 0;
            setSelectedMarketplaceBalance(currentEscrowBalance);
            setIsEscrowBalanceExceed(currentEscrowBalance < 0);
            setAllocatedMonths(
              dspPacing?.allocated_balance?.length
                ? bindEscrowAllocatedBalanceData(dspPacing?.allocated_balance)
                : [
                    {
                      month_year: dayjs(currentDate).format('YYYY-MM-DD'),
                      escrow_allocated_converted_usd: currentEscrowBalance,
                    },
                  ],
            );
          }
          setIsMonthlyDataLoading(false);
        }
      });
    },
    [bindEscrowAllocatedBalanceData, currentDate, customerId],
  );

  // submit the monthly escrow distribution for future months
  const submitAllocatedBudget = useCallback(() => {
    setIsApiCall(true);
    storeAllocatedBudget(allocatedMonths, customerId, selectedMarketplace).then(
      (res) => {
        if (mounted.current) {
          if (res && res.status === 200) {
            onClick();
          }
          setIsApiCall(false);
        }
      },
    );
  }, [allocatedMonths, customerId, selectedMarketplace, onClick]);

  // use effect hook
  useEffect(() => {
    mounted.current = true;
    getEscrowBalanceMarketplace();
    return () => {
      mounted.current = false;
    };
  }, [getEscrowBalanceMarketplace]);

  useEffect(() => {
    mounted.current = true;
    getDSPPacing(marketplace);
    return () => {
      mounted.current = false;
    };
  }, [getDSPPacing, marketplace]);

  // calculate the total of monthly allocated balance
  const calculateSumOfFutureMonths = (newValues) => {
    const sumAll = newValues
      .map((item, index) => index !== 0 && item.escrow_allocated_converted_usd)
      .reduce((prev, curr) => Number(prev) + Number(curr), 0);
    return sumAll;
  };

  // handle monthly allocated balance change events
  const handleEscrowAllocation = (event, currentIndex) => {
    const tempData = [...allocatedMonths];
    const newValue = Number(event?.target?.value?.replace(/,/g, ''));
    const oldValue = tempData[currentIndex]?.escrow_allocated_converted_usd;
    const valueDiff = newValue - oldValue;

    if (newValue > selectedMarketplaceBalance) {
      setIsEscrowBalanceExceed(true);
      setEscrowAllocatedMonthIndex(currentIndex);
    } else if (valueDiff >= 0) {
      setIsEscrowBalanceExceed(false);
      setEscrowAllocatedMonthIndex(null);
      tempData[currentIndex].escrow_allocated_converted_usd = newValue;

      let remainingAmount = valueDiff;

      for (let index = 0; index < allocatedMonths?.length; index += 1) {
        const monthAmount = tempData[index]?.escrow_allocated_converted_usd;
        if (remainingAmount !== 0) {
          if (monthAmount !== 0 && index !== currentIndex) {
            if (remainingAmount > monthAmount) {
              tempData[index].escrow_allocated_converted_usd = Number(0.0);
              remainingAmount -= monthAmount;
            } else {
              const changeInEscrow = monthAmount - remainingAmount;
              tempData[index].escrow_allocated_converted_usd = Number(
                changeInEscrow.toFixed(2),
              );
              remainingAmount = 0.0;
            }
          }
        } else break;
      }
    } else {
      setIsEscrowBalanceExceed(false);
      setEscrowAllocatedMonthIndex(null);
      tempData[currentIndex].escrow_allocated_converted_usd = newValue;
      const sumOfFutureMonths = calculateSumOfFutureMonths(tempData);
      const currentMonthAmount = selectedMarketplaceBalance - sumOfFutureMonths;

      tempData[0].escrow_allocated_converted_usd = Number(
        currentMonthAmount.toFixed(2),
      );
    }
    tempData[currentIndex].escrow_allocated_converted_usd = newValue;

    setAllocatedMonths(tempData);
  };

  /* ********** backup of handle on change function PLEASE DO NOT REMOVE BELOW CODE ********** */
  // const handleOnChange = (event, index) => {
  //   const tempData = [...allocatedMonths];
  //   tempData[index].escrow_allocated_converted_usd = event.target.value.replace(
  //     /,/g,
  //     '',
  //   );
  //   const sumOfFutureMonths = calculateSumOfFutureMonths(tempData);
  //   const currentMonthAmount = selectedMarketplaceBalance - sumOfFutureMonths;
  //   tempData[0].escrow_allocated_converted_usd = String(
  //     currentMonthAmount.toFixed(2),
  //   );
  //   if (currentMonthAmount < 0) setIsEscrowBalanceExceed(true);
  //   else setIsEscrowBalanceExceed(false);
  //   setAllocatedMonths(tempData);
  // };

  // handle add another month events
  const handleOnAddAnotherMonth = () => {
    const lastRecord = allocatedMonths[allocatedMonths.length - 1];
    const nextMonth = new Date(lastRecord?.month_year);
    nextMonth.setHours(nextMonth.getHours() + 12);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    setAllocatedMonths([
      ...allocatedMonths,
      {
        month_year: dayjs(nextMonth).format('YYYY-MM-DD'),
        escrow_allocated_converted_usd: addThousandSeperator(0),
      },
    ]);
  };

  // rendering the allocated escrow balance with edit marketplace reallocation button
  const renderAllocateBar = () => {
    return (
      <div className="col-12" key="allocateBar01">
        <AllocateBar
          id="BT-escrowBalance-budgetAllocaion"
          className="mt-3 mb-3"
        >
          <div className="row">
            <div className="col-12 col-md-7">
              <div className="remaing-label text-bold">
                Escrow Balance:&nbsp;
                {selectedMarketplaceBalance === 0
                  ? `$${selectedMarketplaceBalance}`
                  : addThousandSeperator(
                      selectedMarketplaceBalance,
                      'currency',
                    )}
              </div>
            </div>
            <div className="col-12 col-md-5 d-none ">
              {escrowBalanceMarketplace?.length > 1 ? (
                <div className="text-bold text-right">
                  <div
                    className={
                      !isAllowToEdit
                        ? 'edit-marketplace cursor disabled'
                        : 'edit-marketplace cursor'
                    }
                    role="presentation"
                    onClick={() => {
                      setShowMarketPlaceAllocation(true);
                      setShowEscrowMonthlyAllocation(false);
                    }}
                  >
                    Edit marketplace Allocation
                    <img
                      width="16px"
                      className="orange-left-arrow"
                      src={LeftArrowIcon}
                      alt=""
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
          <div className="clear-fix" />
        </AllocateBar>
      </div>
    );
  };

  // render marketplaces tab with scroll bar
  const renderMarketplaceTab = () => {
    const getIndex = escrowBalanceMarketplace.findIndex(
      (item) => item.value === selectedMarketplace,
    );
    if (getIndex >= 0) {
      const splicedObj = escrowBalanceMarketplace.splice(getIndex, 1)[0];
      escrowBalanceMarketplace.splice(0, 0, splicedObj);
    }
    return (
      <>
        <div className="col-12 mt-2 mb-3 d-none" key="scrollableTabs01">
          <Tabs>
            <ul className="tabs scrollable-container" style={{ width: '100%' }}>
              {escrowBalanceMarketplace.map((item) => {
                return (
                  <li
                    id={item.key}
                    key={item.key}
                    className={
                      selectedMarketplace === item.value
                        ? 'active scrollable-tab small-font-size mt-1'
                        : ' scrollable-tab small-font-size mt-1 '
                    }
                    onClick={() => {
                      setSelectedMarketplace(item.value);
                      getDSPPacing(item.value);
                    }}
                    role="presentation"
                  >
                    {item.label}&nbsp; (
                    {item.escrowBalance === 0
                      ? `$${item.escrowBalance}`
                      : addThousandSeperator(item.escrowBalance, 'currency')}
                    )
                  </li>
                );
              })}
            </ul>
          </Tabs>
        </div>
      </>
    );
  };

  // render month and monthly allocated balance
  const renderMonths = () => {
    return allocatedMonths?.map((item, index) => {
      const isDisabled =
        !index ||
        (isEscrowBalanceExceed && index !== escrowAllocatedMonthIndex);
      return (
        <>
          <div className="row" key={item.month_year}>
            <div className="col-md-6 col-12" key={item.month_year}>
              <InputFormField
                id="BT-escrow-month-budgetAllocation"
                className="mt-1 hide-spinner"
              >
                <label htmlFor="emailAddress">
                  Month
                  <br />
                  <input
                    className={
                      isDisabled ? 'disabled form-control ' : 'form-control '
                    }
                    value={dayjs(item.month_year).format('MMMM YYYY')}
                    type="text"
                    readOnly
                  />
                </label>
              </InputFormField>
            </div>
            <div
              className="col-md-6 col-12 mt-1 mb-3"
              key={dayjs(item.month_year).format('MM/DD/YYYY')}
            >
              <InputFormField id="BT-escrow-numberFormat-budgetAllocation">
                <div className="mb-2">
                  <label htmlFor="amount">amount </label>
                </div>
                <div
                  className="input-container"
                  style={{
                    border:
                      index === escrowAllocatedMonthIndex
                        ? '1px solid red'
                        : 'initial',
                  }}
                >
                  <span
                    className={
                      isDisabled
                        ? 'disabled input-icon mt-0'
                        : 'input-icon mt-0'
                    }
                  >
                    $
                  </span>
                  <NumberFormat
                    className={
                      isDisabled
                        ? 'disabled form-control mt-0'
                        : 'form-control mt-0'
                    }
                    name={item.month_year}
                    defaultValue={item.escrow_allocated_converted_usd}
                    value={item.escrow_allocated_converted_usd}
                    onChange={(event) => handleEscrowAllocation(event, index)}
                    placeholder={0}
                    thousandSeparator
                    decimalScale={2}
                    allowNegative={!index}
                  />
                </div>
              </InputFormField>
            </div>
          </div>
        </>
      );
    });
  };

  // render add annother month button
  const renderAddAnotherMonth = () => {
    return (
      <div className="row" key="ANOTHERMONTH001">
        <div className="col-12">
          <Button
            style={{ textTransform: 'uppercase' }}
            className={
              allocatedMonths?.length > 6 || selectedMarketplaceBalance < 0
                ? 'btn-add-contact  disabled'
                : 'btn-add-contact '
            }
            onClick={() => handleOnAddAnotherMonth()}
          >
            <img
              width="14px"
              style={{ verticalAlign: 'middle' }}
              src={AddIcons}
              className="ml-1 add-icon"
              alt="add"
            />{' '}
            Add another Month
          </Button>
        </div>
      </div>
    );
  };

  // render error message
  const renderErrorMessageBox = (errorMessage) => {
    return (
      <ErrorMsgBox id="scrollToElement" className="mt-2">
        <img className="info-icon" src={InfoRedIcon} alt="info" />
        {errorMessage}
      </ErrorMsgBox>
    );
  };

  // render modal footer
  const renderModalFooter = () => {
    return (
      <div className="modal-footer">
        <div className="row">
          <div className="col-12">
            <Button
              className={
                isEscrowBalanceExceed || isDataLoading
                  ? 'btn-primary on-boarding  w-100 disabled'
                  : 'btn-primary on-boarding  w-100'
              }
              onClick={() => submitAllocatedBudget()}
              type="button"
            >
              {isApiCall ? (
                <PageLoader color="#fff" type="button" />
              ) : (
                'Confirm'
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  /* ********** main return statement of this component ********** */
  return (
    <>
      <Modal
        id={id}
        isOpen={isOpen}
        style={{ ...customStyles }}
        ariaHideApp={false}
        contentLabel="Edit modal"
      >
        <img
          src={CloseIcon}
          alt="close"
          className="float-right cursor cross-icon"
          onClick={() => {
            onClick();
          }}
          role="presentation"
        />
        <ModalBox>
          <>
            {showEscrowMonthlyAllocation ? (
              <>
                <div className="modal-body pb-0">
                  <h4>Allocate Balance</h4>
                  <div className="row my-2" key="renderBar00">
                    {renderAllocateBar()}
                    {escrowBalanceMarketplace?.length > 0
                      ? renderMarketplaceTab()
                      : null}
                  </div>

                  <div
                    className="body-content mt-2"
                    style={{ minHeight: '50px', maxHeight: '450px' }}
                  >
                    {isDataLoading ? (
                      <PageLoader
                        type="page"
                        width={40}
                        height={55}
                        color="#FF5933"
                        component="escrow-modal-loader"
                      />
                    ) : (
                      <div
                        className="row"
                        key="renderMonth00"
                        style={{ minHeight: '60px' }}
                      >
                        {isMonthlyDataLoading ? (
                          <PageLoader
                            component="escrow-modal-loader"
                            color="#FF5933"
                            type="page"
                          />
                        ) : (
                          <>
                            <div className="col-12">
                              {renderMonths()}
                              {renderAddAnotherMonth()}
                            </div>
                            <div className="col-12 mb-3">
                              {isEscrowBalanceExceed
                                ? renderErrorMessageBox(
                                    'All budgets across the selected months need to add up to the available escrow balance',
                                  )
                                : null}
                            </div>
                          </>
                        )}

                        {/* ---------- PLEASE DO NOT REMOVE BELOW CODE..... ---------- */}

                        {/* ) : escrowBalanceMarketplace?.length > 0 ? (
                        <div className="col-12">
                          {renderMonths()}
                          {renderAddAnotherMonth()}
                          {isEscrowBalanceExceed
                            ? renderErrorMessageBox()
                            : null}
                        </div>
                        ) : null} */}
                      </div>
                    )}
                  </div>
                </div>

                <div className="footer-line" />
                {renderModalFooter()}
              </>
            ) : null}
            <EditMarketplaceAllocation
              id="marketplace-allocation"
              customerId={customerId}
              currencySymbol={currencySymbol}
              currentMonthYear={currentMonthYear}
              selectedMarketplace={selectedMarketplace}
              escrowBalanceMarketplace={escrowBalanceMarketplace}
              setEscrowBalanceMarketplace={setEscrowBalanceMarketplace}
              oldEscrowAllocation={oldEscrowAllocation}
              getEscrowBalanceMarketplace={getEscrowBalanceMarketplace}
              totalEscrowBalance={totalEscrowBalance}
              addThousandSeperator={addThousandSeperator}
              setIsDataLoading={setIsDataLoading}
              getDSPPacing={getDSPPacing}
              showMarketPlaceAllocation={showMarketPlaceAllocation}
              setShowMarketPlaceAllocation={setShowMarketPlaceAllocation}
              setShowEscrowMonthlyAllocation={setShowEscrowMonthlyAllocation}
            />
          </>
        </ModalBox>
      </Modal>
    </>
  );
}

EscrowBudgetAllocationModal.defaultProps = {
  id: '',
  customerId: '',
  marketplace: '',
  isOpen: false,
  onClick: () => {},
};

EscrowBudgetAllocationModal.propTypes = {
  id: string,
  customerId: string,
  marketplace: string,
  isOpen: bool,
  onClick: func,
};
